import { lockDocumentResizeCursor } from 'designSystem/components/lockDocumentResizeCursor'
import {
  MAX_PAGES_WITHOUT_RESIZE,
  maxPageRowsThatFit,
  measurePageRowPitch,
  pageRowsForDrag,
  pageRowsForKey,
  readStoredPageRows,
  writeStoredPageRows,
} from 'features/embeddedSidebar/components/sidebarPageRows'
import {
  type KeyboardEvent,
  type PointerEvent,
  type RefObject,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

export function useSidebarPageRows(
  pageCount: number,
  enabled: boolean
): {
  canResize: boolean
  handleKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void
  handlePointerDown: (event: PointerEvent<HTMLDivElement>) => void
  pagesPaneRef: RefObject<HTMLDivElement>
  rows: number
} {
  const pagesPaneRef = useRef<HTMLDivElement>(null)
  const [storedRows, setStoredRows] = useState(readStoredPageRows)
  const [maxRowsThatFit, setMaxRowsThatFit] = useState(pageCount)
  const preferredRows =
    pageCount > MAX_PAGES_WITHOUT_RESIZE ? storedRows : pageCount
  const rows = Math.min(preferredRows, pageCount, maxRowsThatFit)

  const commitRows = useCallback((next: number) => {
    setStoredRows(next)
    writeStoredPageRows(next)
  }, [])

  const measureRowLimits = useCallback(() => {
    const pagesPane = pagesPaneRef.current
    const rowPitch = pagesPane ? measurePageRowPitch(pagesPane) : undefined
    if (!pagesPane || rowPitch === undefined) return undefined
    return {
      rowPitch,
      maxRows: Math.min(pageCount, maxPageRowsThatFit({ pagesPane, rowPitch })),
    }
  }, [pageCount])

  useLayoutEffect(() => {
    const pagesPane = pagesPaneRef.current
    const container = pagesPane?.parentElement
    if (!enabled || !pagesPane || !container) return

    const updateRowLimit = () => {
      const limits = measureRowLimits()
      if (limits) setMaxRowsThatFit(limits.maxRows)
    }
    updateRowLimit()
    const observer = new ResizeObserver(updateRowLimit)
    observer.observe(container)
    if (pagesPane.previousElementSibling) {
      observer.observe(pagesPane.previousElementSibling)
    }
    return () => observer.disconnect()
  }, [enabled, measureRowLimits])

  const handlePointerDown = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      const limits = measureRowLimits()
      if (!limits) return
      event.preventDefault()
      const handle = event.currentTarget
      const { clientY: startY, pointerId } = event
      handle.setPointerCapture(pointerId)
      handle.dataset.resizing = 'true'
      const unlockCursor = lockDocumentResizeCursor('row-resize')
      let nextRows = rows

      const onMove = (moveEvent: globalThis.PointerEvent) => {
        nextRows = pageRowsForDrag({
          ...limits,
          deltaY: moveEvent.clientY - startY,
          startRows: rows,
        })
        setStoredRows(nextRows)
      }

      const onUp = () => {
        document.removeEventListener('pointermove', onMove)
        document.removeEventListener('pointerup', onUp)
        document.removeEventListener('pointercancel', onUp)
        if (handle.hasPointerCapture(pointerId)) {
          handle.releasePointerCapture(pointerId)
        }
        delete handle.dataset.resizing
        unlockCursor()
        if (nextRows !== rows) commitRows(nextRows)
      }

      document.addEventListener('pointermove', onMove)
      document.addEventListener('pointerup', onUp)
      document.addEventListener('pointercancel', onUp)
    },
    [commitRows, measureRowLimits, rows]
  )

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const next = pageRowsForKey({
        key: event.key,
        maxRows: measureRowLimits()?.maxRows ?? pageCount,
        rows,
      })
      if (next === undefined) return
      event.preventDefault()
      commitRows(next)
    },
    [commitRows, measureRowLimits, pageCount, rows]
  )

  return {
    canResize: pageCount > MAX_PAGES_WITHOUT_RESIZE,
    handleKeyDown,
    handlePointerDown,
    pagesPaneRef,
    rows,
  }
}
